import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Calendar, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DeliveryDatePicker from "@/components/DeliveryDatePicker";
import { useOrderUnits } from "@/lib/useOrderUnits";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const isOrderDeadlinePassed = (schedule) => {
  const dl = schedule?.deadline;
  if (!dl) return false;
  return new Date() > new Date(dl.replace(" ", "T"));
};

export default function SeasonalRecommendationsPage() {
  const COMMON_UNITS = useOrderUnits();
  const [recommendations, setRecommendations] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // rec.id being saved
  // rec.id -> { quantity, unit, delivery_date, usage_date }
  const [orderForms, setOrderForms] = useState({});

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [recRes, schRes] = await Promise.all([
        axios.get(`${API}/seasonal-recommendations`, getAuthHeaders()),
        axios.get(`${API}/delivery-schedules`, getAuthHeaders()),
      ]);
      const activeRecs = (recRes.data || []).filter((rec) => rec.is_active);
      setRecommendations(activeRecs);
      setSchedules(schRes.data || []);
      // フォーム初期化（単位はおすすめ設定 or g）
      const init = {};
      activeRecs.forEach((rec) => {
        init[rec.id] = {
          quantity: "",
          unit: rec.unit || "g",
          delivery_date: "",
          usage_date: "",
        };
      });
      setOrderForms(init);
    } catch (error) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (recId, field, value) => {
    setOrderForms((prev) => ({
      ...prev,
      [recId]: { ...prev[recId], [field]: value },
    }));
  };

  // 発注締切前の納品日のみ選択可
  const orderableSchedules = [...schedules]
    .filter((s) => !isOrderDeadlinePassed(s))
    .sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date));

  const handleOrder = async (rec) => {
    const form = orderForms[rec.id] || {};
    if (!form.quantity || parseFloat(form.quantity) <= 0) {
      toast.error("数量を入力してください");
      return;
    }
    if (!form.delivery_date) {
      toast.error("納品日を選択してください");
      return;
    }
    setSaving(rec.id);
    try {
      const payload = {
        orders: [
          {
            product_name: rec.title,
            quantity: parseFloat(form.quantity),
            unit: form.unit || "g",
            delivery_date: form.delivery_date,
            usage_date: form.usage_date || form.delivery_date,
            notes: "季節のおすすめから発注",
          },
        ],
      };
      await axios.post(`${API}/other-orders/bulk`, payload, getAuthHeaders());
      toast.success(`「${rec.title}」を発注しました`);
      updateForm(rec.id, "quantity", "");
      updateForm(rec.id, "delivery_date", "");
      updateForm(rec.id, "usage_date", "");
    } catch (error) {
      const detail = error?.response?.data?.detail || "発注に失敗しました";
      toast.error(detail);
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-600">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="seasonal-recommendations-page">
      {/* ヘッダー */}
      <div>
        <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
          <Sparkles className="h-8 w-8 text-emerald-600" />
          季節のおすすめ
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          旬の食材や期間限定商品をそのまま発注できます（発注締切前の納品日のみ）
        </p>
      </div>

      {/* グリッド表示 */}
      {recommendations.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-12 text-center">
          <Sparkles className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">現在、おすすめ商品はありません</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recommendations.map((rec) => {
            const form = orderForms[rec.id] || {};
            return (
              <div
                key={rec.id}
                data-testid={`seasonal-card-${rec.id}`}
                className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden hover:border-emerald-400 hover:shadow-lg transition-all group flex flex-col"
              >
                {rec.image_url ? (
                  <div className="relative overflow-hidden">
                    <img
                      src={`${BACKEND_URL}${rec.image_url}`}
                      alt={rec.title}
                      className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 right-3 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      期間限定
                    </div>
                  </div>
                ) : (
                  <div className="relative h-56 bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
                    <Sparkles className="h-16 w-16 text-emerald-300" />
                    <div className="absolute top-3 right-3 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      期間限定
                    </div>
                  </div>
                )}
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-bold text-xl text-slate-800 mb-2">
                    {rec.title}
                  </h3>
                  <div className="text-sm text-slate-600 mb-4 whitespace-pre-line">
                    {rec.description}
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Calendar className="h-4 w-4 text-emerald-600" />
                      <span>
                        {rec.start_date} 〜 {rec.end_date}
                      </span>
                    </div>
                  </div>

                  {/* 発注フォーム */}
                  <div className="mt-auto border-t border-slate-100 pt-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-medium text-slate-600">数量</Label>
                        <Input
                          data-testid={`seasonal-quantity-${rec.id}`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.quantity || ""}
                          onChange={(e) => updateForm(rec.id, "quantity", e.target.value)}
                          placeholder="0.00"
                          className="mt-1 h-9 rounded-md"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-slate-600">単位</Label>
                        <Select
                          value={form.unit || "g"}
                          onValueChange={(value) => updateForm(rec.id, "unit", value)}
                        >
                          <SelectTrigger
                            data-testid={`seasonal-unit-${rec.id}`}
                            className="mt-1 h-9 rounded-md"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COMMON_UNITS.map((unit) => (
                              <SelectItem key={unit} value={unit}>
                                {unit}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-medium text-slate-600">納品日</Label>
                      <div className="mt-1">
                        <DeliveryDatePicker
                          value={form.delivery_date || ""}
                          onChange={(d) => {
                            updateForm(rec.id, "delivery_date", d);
                            if (!form.usage_date) {
                              updateForm(rec.id, "usage_date", d);
                            }
                          }}
                          schedules={orderableSchedules}
                          testId={`seasonal-delivery-${rec.id}`}
                          placeholder="配送日を選択"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-medium text-slate-600">使用予定日</Label>
                      <Input
                        data-testid={`seasonal-usage-${rec.id}`}
                        type="date"
                        value={form.usage_date || form.delivery_date || ""}
                        onChange={(e) => updateForm(rec.id, "usage_date", e.target.value)}
                        className="mt-1 h-9 rounded-md bg-yellow-50"
                      />
                    </div>

                    <Button
                      onClick={() => handleOrder(rec)}
                      disabled={saving === rec.id}
                      data-testid={`seasonal-order-button-${rec.id}`}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      <ShoppingCart className="h-4 w-4 mr-1" />
                      {saving === rec.id ? "発注中..." : "この商品を発注する"}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
